import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Table,
  Button,
  Tag,
  App,
  Space,
  Descriptions,
  Typography,
  Popconfirm,
  Collapse,
  Spin,
  Alert,
  Tooltip,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  EditOutlined,
  CheckOutlined,
  SendOutlined,
  StopOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { useUsers } from '@/hooks/useUsers';
import { purchaseOrderService, type PurchaseOrderOverheadDTO, type PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { supplierAdvanceRequestService } from '@/services/supplierAdvanceRequest.service';
import supplierAdvanceRefundService, { recoveryStatusTagColor } from '@/services/supplierAdvanceRefund.service';
import { approvalService } from '@/services/approval.service';
import { PrintablePurchaseOrder } from '@/components/procurement/PrintablePurchaseOrder';
import { EngineeringWeightCell } from '@/components/procurement/EngineeringWeightCell';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { useSystemSettings } from '@/hooks/useSettings';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { AiApprovalBriefPanel } from '@/components/approval/AiApprovalBriefPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { computePoPrintCommercial, computePoPrintGrandTotal, computePoPrintVatSummary } from '@/utils/purchaseOrderPrint';
import { displayPoRevisionLabel, isPoRevision } from '@/utils/purchaseOrderRevision';
import { headerProjectLabel, linesCarryProjects, projectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { lineSpecificationsExpandable } from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_ORDER } from '@/constants/document-entity-types';
import { poMilestoneService } from '@/services/poMilestone.service';
import {
  PO_PAYMENT_TERM_MILESTONE,
  PO_MILESTONE_FULFILLMENT_LABELS,
  PO_MILESTONE_STATUS_COLORS,
} from '@/constants/poMilestoneTypes';
import { BudgetExceedanceRequestModal } from '@/components/project/BudgetExceedanceRequestModal';
import { BudgetCodeLabel } from '@/components/project/BudgetCodeLabel';
import { BudgetSpendStatus } from '@/components/project/BudgetSpendStatus';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import type { User } from '@/services/user.service';
import CreateEnvelopeChildModal from '@/components/procurement/CreateEnvelopeChildModal';
import {
  envelopeChildActionLabel,
  isEnvelopePoType,
  normalizePoType,
  PO_TYPE_STANDARD,
} from '@/constants/purchaseOrderTypes';

const { Text, Title } = Typography;

function getTaxTypeLabel(vat_percentage: number | undefined, vat_applicable: boolean | undefined): string {
  if (vat_applicable === false) return 'Exempt';
  if (vat_percentage === 0) return 'VAT 0%';
  if (vat_percentage != null) return `VAT ${Number(vat_percentage)}%`;
  return 'VAT 5%';
}

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  'Sent to Supplier': 'cyan',
  Acknowledged: 'cyan',
  'Partially Received': 'orange',
  'Fully Received': 'green',
  Closed: 'default',
  Cancelled: 'red',
};

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [exceedanceOpen, setExceedanceOpen] = useState(false);
  const [exceedanceCheck, setExceedanceCheck] = useState<Record<string, unknown> | null>(null);
  const [exceedanceProjectId, setExceedanceProjectId] = useState<string>('');
  const [exceedanceLines, setExceedanceLines] = useState<Array<Record<string, unknown>>>([]);
  const [sarDeptModalOpen, setSarDeptModalOpen] = useState(false);
  const [sarDepartmentId, setSarDepartmentId] = useState<string | undefined>();
  const [sarCreating, setSarCreating] = useState(false);
  const [envelopeChildOpen, setEnvelopeChildOpen] = useState(false);
  const { isCriticalCode } = useCriticalItemLookup();
  const { data: users = [] } = useUsers({ is_active: true });

  const { data: po, isLoading } = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => purchaseOrderService.getById(id!),
    enabled: !!id,
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const spotPurchaserName = useMemo(() => {
    const uid = po?.spot_purchaser_user_id;
    if (!uid) return null;
    const map = buildUserIdToLabelMap(users as User[]);
    return resolveUserDisplayFromMap(map, uid);
  }, [po, users]);

  const { data: items = [] } = useQuery({
    queryKey: ['purchase-order-items', id],
    queryFn: () => purchaseOrderService.getItems(id!),
    enabled: !!id,
  });

  const poProjectId = useMemo(() => {
    if (!po) return '';
    return (
      String((po as any).project_id || '') ||
      String((items as any[]).find((i) => i.project_id)?.project_id || '')
    );
  }, [po, items]);

  const budgetCheckQ = useQuery({
    queryKey: ['po-budget-check', id, items],
    queryFn: async () => {
      const byProject = new Map<string, any[]>();
      for (const it of items as any[]) {
        const pid = String(it.project_id || poProjectId || '');
        if (!pid) continue;
        const list = byProject.get(pid) || [];
        list.push(it);
        byProject.set(pid, list);
      }
      const results: Array<{ projectId: string; check: Record<string, unknown> }> = [];
      for (const [projectId, list] of byProject) {
        const check = await projectService.checkCostControl(projectId, {
          lines: list.map((it) => ({
            projectId,
            wbsNodeId: it.wbs_node_id || null,
            cbsCodeId: it.cbs_code_id || null,
            costCategory: it.cost_category || 'Material',
            amount:
              Number(it.net_amount) ||
              Number(it.line_total) ||
              Number(it.unit_price || 0) * Number(it.ordered_quantity || 0),
            sourceLineId: it.id,
          })),
          excludeSourceType: 'purchase_order',
          excludeSourceId: id,
          sourceEntityType: 'purchase_order',
          sourceEntityId: id,
        });
        results.push({ projectId, check });
      }
      return results;
    },
    enabled: !!id && !!po && items.length > 0 && !!poProjectId,
    staleTime: 15_000,
  });

  const budgetCovered = useMemo(() => {
    const rows = budgetCheckQ.data || [];
    return rows.some((r) => !!(r.check as any)?.coveredBy);
  }, [budgetCheckQ.data]);

  const budgetPendingExceedance = useMemo(() => {
    const rows = budgetCheckQ.data || [];
    return rows.find((r) => (r.check as any)?.pendingExceedance)?.check as
      | { pendingExceedance?: { id?: string; status?: string } }
      | undefined;
  }, [budgetCheckQ.data]);

  const budgetBreachActive = useMemo(() => {
    const rows = budgetCheckQ.data || [];
    return rows.some((r) => (r.check as any)?.active && !(r.check as any)?.ok);
  }, [budgetCheckQ.data]);

  const budgetOverTotal = useMemo(() => {
    const rows = budgetCheckQ.data || [];
    return rows.reduce((s, r) => s + (Number((r.check as any)?.totals?.over) || 0), 0);
  }, [budgetCheckQ.data]);

  const coveredExceedanceId = useMemo(() => {
    const rows = budgetCheckQ.data || [];
    for (const r of rows) {
      const id = (r.check as any)?.coveredBy?.id;
      if (id) return String(id);
    }
    return '';
  }, [budgetCheckQ.data]);

  const needsExceedanceRequest =
    budgetBreachActive && !budgetCovered && !budgetPendingExceedance?.pendingExceedance;

  const { data: systemSettings } = useSystemSettings();

  const poVatSummary = useMemo(
    () => (po ? computePoPrintVatSummary(po, items) : null),
    [po, items]
  );
  const poCommercial = useMemo(
    () => (po ? computePoPrintCommercial(po, items) : null),
    [po, items]
  );
  const displayGrandTotal = useMemo(
    () => (po && poVatSummary ? computePoPrintGrandTotal(po, items, poVatSummary) : 0),
    [po, items, poVatSummary]
  );

  const isMilestoneBased = po?.payment_term_type === PO_PAYMENT_TERM_MILESTONE;

  const { data: milestones = [] } = useQuery({
    queryKey: ['po-milestones', id],
    queryFn: () => poMilestoneService.listForPo(id!),
    enabled: !!id && isMilestoneBased,
  });

  const { data: advanceSummary } = useQuery({
    queryKey: ['po-advance-summary', id],
    queryFn: () => purchaseOrderService.getAdvanceSummary(id!),
    enabled: !!id && !!po?.advance_required,
  });

  const { data: recoveryExposures = [] } = useQuery({
    queryKey: ['po-recovery-exposures', id],
    queryFn: () => supplierAdvanceRefundService.findAllExposures({ purchase_order_id: id! }),
    enabled: !!id && po?.po_status === 'Cancelled' && Number(advanceSummary?.paid ?? 0) > 0,
  });

  const showRecoveryPanel =
    po?.po_status === 'Cancelled' &&
    (po.advance_recovery_status || recoveryExposures.length > 0 || Number(advanceSummary?.paid ?? 0) > 0);

  const openExceedanceFromPo = async () => {
    if (!po || !id) return;
    const projectId =
      String(po.project_id || '') ||
      String(items.find((i: any) => i.project_id)?.project_id || '');
    if (!projectId) {
      message.warning('Assign a project on PO lines before requesting budget exceedance');
      return;
    }
    const lines = (items as any[]).map((it) => ({
      projectId: it.project_id || projectId,
      wbsNodeId: it.wbs_node_id || null,
      cbsCodeId: it.cbs_code_id || null,
      costCategory: it.cost_category || 'Material',
      amount:
        Number(it.net_amount) ||
        Number(it.line_total) ||
        Number(it.unit_price || 0) * Number(it.ordered_quantity || 0),
      sourceLineId: it.id,
    }));
    try {
      const check = await projectService.checkCostControl(projectId, {
        lines,
        excludeSourceType: 'purchase_order',
        excludeSourceId: id,
        sourceEntityType: 'purchase_order',
        sourceEntityId: id,
      });
      setExceedanceProjectId(projectId);
      setExceedanceLines(lines);
      setExceedanceCheck(check);
      setExceedanceOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not check budget');
    }
  };

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    setActionLoading(key);
    try {
      await fn();
      message.success('Done');
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    } catch (e: any) {
      const code = e?.response?.data?.errors?.code || e?.response?.data?.code;
      const msg = e?.response?.data?.message || 'Action failed';
      if (key === 'submit' && (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(String(msg)))) {
        message.warning(msg);
        await openExceedanceFromPo();
      } else {
        message.error(msg);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = async () => {
    if (!po || !items.length) return;
    const companySettings = systemSettings?.filter((s) => s.category === 'company') ?? [];
    const getSetting = (key: string) => companySettings.find((s) => s.setting_key === key)?.setting_value ?? '';
    const trn = getSetting('tax_registration_number');
    const companyInfo = {
      name: getSetting('company_name') || 'Company',
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: trn ? `TRN: ${trn}` : '',
      logo: getSetting('company_logo') || undefined,
    };

    let approvalSigners: Awaited<ReturnType<typeof approvalService.getPrintSigners>> = [];
    if (po.approval_instance_id) {
      try {
        approvalSigners = await approvalService.getPrintSigners('purchase_order', po.id);
      } catch {
        approvalSigners = [];
      }
    }

    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(
      <PrintablePurchaseOrder
        po={po}
        items={items}
        companyInfo={companyInfo}
        approvalSigners={approvalSigners}
        spotPurchaserName={spotPurchaserName}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const breadcrumbSegments = useMemo(
    () =>
      po
        ? [
            { title: 'Supply Chain', path: '/scm/dashboard' },
            { title: 'Purchase orders', path: '/scm/procurement/purchase-orders' },
            { title: po.po_number || 'PO' },
          ]
        : null,
    [po?.id, po?.po_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const poWorkflowStages = useMemo(() => {
    if (!po) return [];
    const st = po.po_status;
    const cancelled = st === 'Cancelled';
    const fullyReceived = st === 'Fully Received' || st === 'Closed';
    const partial = st === 'Partially Received';
    return [
      {
        title: 'Purchase order',
        description: st,
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Goods receipt',
        description: partial || fullyReceived ? 'In progress / done' : 'Pending',
        status: (cancelled ? 'wait' : fullyReceived ? 'finish' : partial ? 'process' : st !== 'Draft' ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Purchase bill',
        description: 'Supplier invoice',
        status: (fullyReceived ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Payment',
        description: 'AP settlement',
        status: 'wait' as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [po]);

  const poWorkflowCurrent = useMemo(() => {
    if (!po) return 0;
    const st = po.po_status;
    if (st === 'Draft') return 0;
    if (st === 'Cancelled') return 0;
    if (st === 'Fully Received' || st === 'Closed') return 2;
    if (st === 'Partially Received') return 1;
    return 1;
  }, [po]);

  const handleCreateAmendment = async () => {
    setActionLoading('amendment');
    try {
      const newPO = await purchaseOrderService.createAmendment(id!);
      message.success('Amendment created');
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      if (newPO?.id) navigate(`/scm/procurement/purchase-orders/${newPO.id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to create amendment');
    } finally {
      setActionLoading(null);
    }
  };

  if (!id) return null;
  if (isLoading && !po) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      {id ? <AiPageContextSync module="procurement" docType="PurchaseOrder" docId={id} /> : null}
        <Spin size="large" />
      </div>
    );
  }
  if (!po) return <Card><p>Purchase order not found.</p></Card>;

  const isDraft = po.po_status === 'Draft';
  const canSubmit = isDraft;
  const showUniversalApproval = !!po.approval_instance_id;
  // Legacy one-shot Approve only when Submitted and no workflow instance.
  // Draft must use Submit so the Platform multi-step workflow can start (same as SO/PV).
  const canApprove = po.po_status === 'Submitted' && !showUniversalApproval;
  const canSend = po.approval_status === 'Approved' && po.po_status === 'Approved';
  const canCancel = po.po_status !== 'Cancelled' && po.po_status !== 'Fully Received';
  const canAmendment = po.po_status !== 'Cancelled';
  const isEnvelopeParent = isEnvelopePoType(po.po_type) && !po.parent_po_id;
  const envelopeAction = envelopeChildActionLabel(po.po_type);
  const canCreateEnvelopeChild =
    isEnvelopeParent &&
    po.approval_status === 'Approved' &&
    po.po_status !== 'Cancelled' &&
    po.po_status !== 'Closed';

  const primaryActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-orders')}>
        Back
      </Button>
      {(po.po_status === 'Draft' || po.po_status === 'Submitted') && (
        <Button type="default" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/purchase-orders/${po.id}/edit`)}>
          Edit
        </Button>
      )}
      <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
        Print
      </Button>
      <ReportDocumentActions
        reportKey="purchase-order"
        params={{ id: po.id }}
        documentId={po.id}
        sendPermissions={['procurement.po.send', 'procurement.po.edit']}
        emailLabel="Email supplier"
      />
      {canSubmit && (
        <Button
          type="primary"
          loading={actionLoading === 'submit'}
          onClick={() =>
            runAction('submit', async () => {
              await purchaseOrderService.submit(id!);
              await queryClient.invalidateQueries({ queryKey: ['approval-summary', 'purchase_order', id] });
              await queryClient.invalidateQueries({ queryKey: ['ai-approval-brief', 'purchase_order', id] });
            })
          }
        >
          Submit for approval
        </Button>
      )}
      {canApprove && (
        <Button
          type="primary"
          loading={actionLoading === 'approve'}
          icon={<CheckOutlined />}
          onClick={() => runAction('approve', () => purchaseOrderService.approve(id!))}
        >
          Approve
        </Button>
      )}
      {canSend && (
        <Button
          icon={<SendOutlined />}
          loading={actionLoading === 'send'}
          onClick={() => runAction('send', () => purchaseOrderService.sendToSupplier(id!))}
        >
          Send to Supplier
        </Button>
      )}
      {canCancel && (
        <Popconfirm title="Cancel this purchase order?" onConfirm={() => runAction('cancel', () => purchaseOrderService.cancel(id!))}>
          <Button danger icon={<StopOutlined />} loading={actionLoading === 'cancel'}>
            Cancel
          </Button>
        </Popconfirm>
      )}
      {canAmendment && (
        <Button icon={<CopyOutlined />} loading={actionLoading === 'amendment'} onClick={handleCreateAmendment}>
          Create amendment
        </Button>
      )}
      {canCreateEnvelopeChild && (
        <Button type="primary" onClick={() => setEnvelopeChildOpen(true)}>
          {envelopeAction === 'call-off' ? 'Create call-off' : 'Create release'}
        </Button>
      )}
      {(po.po_status === 'Draft' || po.po_status === 'Submitted') && needsExceedanceRequest && (
        <Button onClick={() => void openExceedanceFromPo()}>Request budget exceedance</Button>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>Quick links</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/scm/procurement/grn')}>
          Goods receipt (GRN)
        </Button>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/scm/procurement/supplier-invoices')}>
          Supplier invoices
        </Button>
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      {id ? <AiPageContextSync module="procurement" docType="PurchaseOrder" docId={id} /> : null}
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Purchase order"
            documentNumber={po.po_number}
            statusTag={
              <Space>
                <Tag color={statusColors[po.po_status] ?? 'default'}>{po.po_status}</Tag>
                <Tag>{po.approval_status}</Tag>
                {normalizePoType(po.po_type) !== PO_TYPE_STANDARD ? (
                  <Tag color="purple">{normalizePoType(po.po_type)}</Tag>
                ) : null}
                {po.procurement_method === 'MARKET_PURCHASE' ? (
                  <Tag color="teal">Spot / Market Purchase</Tag>
                ) : null}
                {isPoRevision(po.revision_number) && (
                  <Tag color="geekblue">Revision {displayPoRevisionLabel(po.revision_number)}</Tag>
                )}
              </Space>
            }
            subtitle={
              <Text type="secondary">
                {po.po_date ? dayjs(po.po_date).format('DD MMM YYYY') : ''}
                {po.procurement_method === 'MARKET_PURCHASE' && spotPurchaserName
                  ? ` · Market purchaser: ${spotPurchaserName}`
                  : ''}
              </Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>Supplier:</strong> {po.supplier_name}
                </Text>
                {po.procurement_method === 'MARKET_PURCHASE' ? (
                  <Text>
                    <strong>Market purchaser:</strong> {spotPurchaserName || '—'}
                  </Text>
                ) : null}
                <Text>
                  <strong>Total:</strong> {po.currency}{' '}
                  {displayGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Text>
              </Space>
            }
            primaryActions={primaryActions}
          />
        }
        workflow={<WorkflowStrip stages={poWorkflowStages} current={poWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        {isMilestoneBased && (
          <Card
            title="Milestone fulfillment"
            style={{ marginBottom: 16 }}
            extra={
              <Button size="small" onClick={() => navigate('/scm/procurement/reports/po-milestones')}>
                Milestone register
              </Button>
            }
          >
            <Descriptions size="small" column={4} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Milestone total">
                {po.currency} {Number(po.milestone_total ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Invoiced">
                {Number(po.milestone_invoiced_value ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Paid">
                {Number(po.milestone_paid_value ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Balance">
                {Number(po.milestone_balance ?? 0).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              pagination={false}
              rowKey="id"
              dataSource={[...milestones].sort((a, b) => a.milestone_number - b.milestone_number)}
              columns={[
                { title: '#', dataIndex: 'milestone_number', width: 40 },
                { title: 'Name', dataIndex: 'milestone_name' },
                { title: 'Type', dataIndex: 'milestone_type' },
                {
                  title: 'Fulfillment',
                  dataIndex: 'fulfillment_method',
                  render: (v) => PO_MILESTONE_FULFILLMENT_LABELS[v] ?? v,
                },
                {
                  title: 'Amount',
                  dataIndex: 'milestone_amount',
                  align: 'right' as const,
                  render: (v) => Number(v).toFixed(2),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  render: (s) => <Tag color={PO_MILESTONE_STATUS_COLORS[s] ?? 'default'}>{s}</Tag>,
                },
                {
                  title: 'Actions',
                  render: (_, m) => (
                    <Space size="small" wrap>
                      {m.fulfillment_method === 'SupplierAdvance' && (
                        <Button
                          type="link"
                          size="small"
                          onClick={async () => {
                            try {
                              const sar = await poMilestoneService.createAdvanceFromMilestone(id!, m.id);
                              message.success(`Created ${sar.advance_request_number}`);
                              queryClient.invalidateQueries({ queryKey: ['po-milestones', id] });
                              navigate(`/scm/procurement/supplier-advance-requests/${sar.id}`);
                            } catch (e: any) {
                              message.error(e?.response?.data?.message || 'Failed');
                            }
                          }}
                        >
                          Advance request
                        </Button>
                      )}
                      {m.fulfillment_method === 'ServiceReceipt' && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            navigate(`/scm/procurement/service-receipts/new?po_id=${id}&milestone_id=${m.id}`)
                          }
                        >
                          Service receipt
                        </Button>
                      )}
                      {m.fulfillment_method === 'GRN' && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => navigate(`/scm/procurement/grn/new?po_id=${id}&milestone_id=${m.id}`)}
                        >
                          GRN
                        </Button>
                      )}
                      {m.status === 'Completed' && (
                        <Button
                          type="link"
                          size="small"
                          onClick={async () => {
                            try {
                              await poMilestoneService.applyAction(m.id, 'accept');
                              message.success('Milestone accepted');
                              queryClient.invalidateQueries({ queryKey: ['po-milestones', id] });
                              queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
                            } catch (e: any) {
                              message.error(e?.response?.data?.message || 'Failed');
                            }
                          }}
                        >
                          Accept
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        )}
        {(po.advance_required || Number(po.advance_payment_amount) > 0) && (
          <Card title="Supplier advance" style={{ marginBottom: 16 }} extra={<Tag>{po.advance_status || '—'}</Tag>}>
            <Descriptions size="small" column={3}>
              <Descriptions.Item label="Cap">
                {po.currency}{' '}
                {Number(advanceSummary?.advance_cap ?? po.advance_payment_amount ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Requested (total)">
                {Number(advanceSummary?.requested ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Paid">{Number(advanceSummary?.paid ?? 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Open advance">{Number(advanceSummary?.remaining ?? 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Remaining request capacity">
                {Number(advanceSummary?.remaining_request_capacity ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Unpaid on requests">
                {Number(advanceSummary?.unpaid_request_balance ?? 0).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>
            {advanceSummary?.block_reason && !advanceSummary.can_create_request && (
              <Alert type="warning" showIcon message={advanceSummary.block_reason} style={{ marginTop: 12 }} />
            )}
            {(advanceSummary?.requests?.length ?? 0) > 0 && (
              <Table
                size="small"
                style={{ marginTop: 12 }}
                pagination={false}
                rowKey="id"
                dataSource={advanceSummary?.requests ?? []}
                columns={[
                  {
                    title: 'Request #',
                    dataIndex: 'advance_request_number',
                    render: (num: string, row: { id: string }) => (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => navigate(`/scm/procurement/supplier-advance-requests/${row.id}`)}
                      >
                        {num}
                      </Button>
                    ),
                  },
                  { title: 'Status', dataIndex: 'status' },
                  {
                    title: 'Requested',
                    dataIndex: 'requested_amount',
                    align: 'right' as const,
                    render: (v: number) => Number(v).toFixed(2),
                  },
                  {
                    title: 'Paid',
                    dataIndex: 'paid_amount',
                    align: 'right' as const,
                    render: (v: number) => Number(v).toFixed(2),
                  },
                ]}
              />
            )}
            <Space style={{ marginTop: 12 }}>
              <Tooltip title={advanceSummary?.block_reason ?? undefined}>
                <Button
                  type="primary"
                  disabled={
                    !po.advance_required ||
                    po.po_status === 'Cancelled' ||
                    advanceSummary?.can_create_request === false
                  }
                  onClick={async () => {
                    const existingDept = po.department_id ? String(po.department_id).trim() : '';
                    if (!existingDept) {
                      setSarDepartmentId(undefined);
                      setSarDeptModalOpen(true);
                      return;
                    }
                    try {
                      const sar = await supplierAdvanceRequestService.createFromPo(id!, {
                        department_id: existingDept,
                      });
                      message.success(`Created ${sar.advance_request_number}`);
                      queryClient.invalidateQueries({ queryKey: ['po-advance-summary', id] });
                      navigate(`/scm/procurement/supplier-advance-requests/${sar.id}`);
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || 'Failed to create advance request');
                    }
                  }}
                >
                  Create advance request
                </Button>
              </Tooltip>
              <Button onClick={() => navigate(`/scm/procurement/supplier-advance-requests?purchase_order_id=${id}`)}>
                View advances
              </Button>
            </Space>
          </Card>
        )}
        {showRecoveryPanel && (
          <Card
            title="Advance recovery"
            style={{ marginBottom: 16 }}
            extra={
              po.advance_recovery_status ? (
                <Tag color={recoveryStatusTagColor(po.advance_recovery_status)}>{po.advance_recovery_status}</Tag>
              ) : null
            }
          >
            <Descriptions size="small" column={3}>
              <Descriptions.Item label="Recovery status">
                {po.advance_recovery_status ? (
                  <Tag color={recoveryStatusTagColor(po.advance_recovery_status)}>{po.advance_recovery_status}</Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Advance paid">{Number(advanceSummary?.paid ?? 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Open exposures">{recoveryExposures.length}</Descriptions.Item>
            </Descriptions>
            {recoveryExposures.length > 0 && (
              <Table
                size="small"
                style={{ marginTop: 12 }}
                pagination={false}
                rowKey="id"
                dataSource={recoveryExposures}
                columns={[
                  { title: 'SAR', dataIndex: 'advance_request_number' },
                  {
                    title: 'Balance',
                    dataIndex: 'remaining_refundable_balance',
                    align: 'right' as const,
                    render: (v: number, row: { currency?: string }) =>
                      `${row.currency ?? po.currency} ${Number(v).toFixed(2)}`,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'recovery_status',
                    render: (status: string) => <Tag color={recoveryStatusTagColor(status)}>{status}</Tag>,
                  },
                ]}
              />
            )}
            <Space style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={() => navigate(`/finance/ap/supplier-advance-refunds?purchase_order_id=${id}`)}
              >
                View refund vouchers
              </Button>
              {recoveryExposures.length === 1 && (
                <Button
                  onClick={() =>
                    navigate(`/finance/ap/supplier-advance-refunds/new?exposure_id=${recoveryExposures[0].id}`)
                  }
                >
                  Create refund
                </Button>
              )}
            </Space>
          </Card>
        )}
        <TaskRoutingCard
          sourceDocumentType="PurchaseOrder"
          sourceDocumentId={po.id}
          nextActionUserId={po.next_action_user_id}
          documentOwnerLabel="Buyer"
          documentOwnerUserId={po.buyer}
          disabled={po.po_status === 'Cancelled' || po.po_status === 'Closed'}
          invalidateQueryKeys={[['purchase-order', id!]]}
        />
        <div style={{ marginBottom: 16 }}>
          <AiApprovalBriefPanel
            entityType="purchase_order"
            entityId={po.id}
            defaultExpanded={false}
          />
          {(showUniversalApproval || po.po_status === 'Submitted') && (
            <ApprovalPanel
              entityType="purchase_order"
              entityId={po.id}
              showAiBrief={false}
              onApproveViaDocument={async (remarks) => {
                await purchaseOrderService.approve(id!, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await purchaseOrderService.rejectApproval(id!, payload);
              }}
              extraInvalidateQueryKeys={[['purchase-order', id], ['purchase-orders']]}
            />
          )}
        </div>
        {budgetCovered && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="Budget exceedance approved"
            description={
              <Space direction="vertical" size={4}>
                <Text>
                  Overspend is authorized
                  {budgetOverTotal > 0 ? ` (over by ${formatMoney(budgetOverTotal)})` : ''}. You can
                  submit this PO; Current Budget is unchanged.
                </Text>
                {coveredExceedanceId ? (
                  <Link to={`/prj/budget-exceedances/${encodeURIComponent(coveredExceedanceId)}`}>
                    View exceedance
                  </Link>
                ) : null}
              </Space>
            }
          />
        )}
        {!budgetCovered && budgetPendingExceedance?.pendingExceedance && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Budget exceedance pending approval"
            description={
              <Space direction="vertical" size={4}>
                <Text>
                  An exceedance request is awaiting approval
                  {budgetOverTotal > 0 ? ` (over by ${formatMoney(budgetOverTotal)})` : ''}. Submit
                  remains blocked until it is approved.
                </Text>
                {budgetPendingExceedance.pendingExceedance.id ? (
                  <Link
                    to={`/prj/budget-exceedances/${encodeURIComponent(
                      String(budgetPendingExceedance.pendingExceedance.id)
                    )}`}
                  >
                    View exceedance
                  </Link>
                ) : null}
              </Space>
            }
          />
        )}
        {needsExceedanceRequest && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              (budgetCheckQ.data || []).some((r) => (r.check as any)?.mode === 'estimation')
                ? 'Estimation cost exceedance'
                : 'Budget exceedance'
            }
            description={
              <Space direction="vertical" size={4}>
                <Text>
                  {(budgetCheckQ.data || []).some((r) => (r.check as any)?.mode === 'estimation')
                    ? 'This PO exceeds the approved estimated project cost'
                    : 'One or more lines exceed available Project Cost Budget'}
                  {budgetOverTotal > 0 ? ` by ${formatMoney(budgetOverTotal)}` : ''}. Submit is blocked until
                  {(budgetCheckQ.data || []).some((r) => (r.check as any)?.mode === 'estimation')
                    ? ' you request a Budget Exceedance.'
                    : ' you remap WBS/category or request a Budget Exceedance.'}
                </Text>
                {(po.po_status === 'Draft' || po.po_status === 'Submitted') && (
                  <Button size="small" type="primary" danger onClick={() => void openExceedanceFromPo()}>
                    Request budget exceedance
                  </Button>
                )}
              </Space>
            }
          />
        )}
        <Collapse
          defaultActiveKey={['detail', 'lines']}
          items={[
            {
              key: 'detail',
              label: 'PO details & commercial',
              children: (
                <>
                  <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                    <Descriptions.Item label="Supplier">{po.supplier_name}</Descriptions.Item>
                    <Descriptions.Item label="PO type">{normalizePoType(po.po_type)}</Descriptions.Item>
                    <Descriptions.Item label="Procurement method">
                      {po.procurement_method === 'MARKET_PURCHASE' ? (
                        <Tag color="teal">Spot / Market Purchase</Tag>
                      ) : (
                        po.procurement_method || 'Standard'
                      )}
                    </Descriptions.Item>
                    {po.spot_purchaser_user_id ? (
                      <Descriptions.Item label="Market purchaser">
                        {spotPurchaserName}
                      </Descriptions.Item>
                    ) : null}
                    {linesCarryProjects(items) ? null : (
                      <Descriptions.Item label="Project">{headerProjectLabel(po, items)}</Descriptions.Item>
                    )}
                    <Descriptions.Item label="Department">
                      {po.department_id
                        ? (() => {
                            const d = departments.find((x) => x.id === po.department_id);
                            return d ? `${d.department_code} - ${d.department_name}` : po.department_id;
                          })()
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="PO date">{po.po_date ? dayjs(po.po_date).format('DD MMM YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Delivery date">
                      {po.delivery_date ? dayjs(po.delivery_date).format('DD MMM YYYY') : '—'}
                    </Descriptions.Item>
                    {isEnvelopeParent || po.agreement_start_date || po.agreement_end_date || po.max_value != null ? (
                      <>
                        <Descriptions.Item label="Agreement start">
                          {po.agreement_start_date ? dayjs(po.agreement_start_date).format('DD MMM YYYY') : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Agreement end">
                          {po.agreement_end_date ? dayjs(po.agreement_end_date).format('DD MMM YYYY') : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Max value">
                          {po.max_value != null
                            ? `${po.currency} ${Number(po.max_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                            : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Released value">
                          {po.currency}{' '}
                          {Number(po.released_value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Descriptions.Item>
                      </>
                    ) : null}
                    <Descriptions.Item label="Currency">{po.currency}</Descriptions.Item>
                    <Descriptions.Item label="Payment terms">{po.payment_terms || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Payment type">
                      {po.payment_term_type === PO_PAYMENT_TERM_MILESTONE ? 'Milestone-based' : 'Standard'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Subtotal">
                      {po.currency} {Number(po.subtotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Descriptions.Item>
                    {poCommercial && poCommercial.discount > 0 && (
                      <Descriptions.Item label={poCommercial.discountLabel}>
                        {po.currency} {poCommercial.discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Descriptions.Item>
                    )}
                    {poVatSummary && !poVatSummary.exempt && poVatSummary.vatAmount > 0 && (
                      <Descriptions.Item label={`VAT (${po.vat_percentage ?? 5}%)`}>
                        {po.currency}{' '}
                        {poVatSummary.vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Descriptions.Item>
                    )}
                    {poVatSummary?.exempt && (
                      <Descriptions.Item label="VAT">Exempt</Descriptions.Item>
                    )}
                    {Number(po.total_overhead ?? 0) > 0 && (
                      <Descriptions.Item label="Total overhead">
                        {po.currency}{' '}
                        {Number(po.total_overhead).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Grand total">
                      <Text strong>
                        {po.currency}{' '}
                        {displayGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                  {Number(po.total_overhead ?? 0) > 0 && Array.isArray(po.overheads) && po.overheads.length > 0 ? (
                    <Table<PurchaseOrderOverheadDTO>
                      size="small"
                      style={{ marginTop: 12 }}
                      pagination={false}
                      rowKey={(r, i) => String(r.id || i)}
                      dataSource={po.overheads}
                      columns={[
                        { title: 'Type', dataIndex: 'overhead_type', width: 140 },
                        {
                          title: 'Description',
                          dataIndex: 'description',
                          render: (v: string | null | undefined) => v || '—',
                        },
                        {
                          title: 'Amount',
                          dataIndex: 'amount',
                          align: 'right' as const,
                          width: 120,
                          render: (_v: number | undefined, r: PurchaseOrderOverheadDTO) =>
                            `${po.currency} ${Number(r.amount ?? r.original_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                        },
                        {
                          title: 'Cleared',
                          key: 'cleared',
                          align: 'right' as const,
                          width: 120,
                          render: (_: unknown, r: PurchaseOrderOverheadDTO) =>
                            `${po.currency} ${Number(r.invoiced_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                        },
                        {
                          title: 'Remaining',
                          key: 'remaining',
                          align: 'right' as const,
                          width: 120,
                          render: (_: unknown, r: PurchaseOrderOverheadDTO) => {
                            const total = Number(r.amount ?? r.original_amount ?? 0);
                            const cleared = Number(r.invoiced_amount ?? 0);
                            const remaining = Math.round((total - cleared) * 100) / 100;
                            return `${po.currency} ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                          },
                        },
                        {
                          title: 'Status',
                          key: 'status',
                          width: 100,
                          render: (_: unknown, r: PurchaseOrderOverheadDTO) => {
                            const total = Number(r.amount ?? r.original_amount ?? 0);
                            const cleared = Number(r.invoiced_amount ?? 0);
                            if (cleared <= 0) return <Tag>Open</Tag>;
                            if (cleared + 0.0001 >= total) return <Tag color="green">Cleared</Tag>;
                            return <Tag color="orange">Partial</Tag>;
                          },
                        },
                      ]}
                    />
                  ) : null}
                  {(po.bidAnalysisSources?.length ||
                    po.bid_analysis_id ||
                    po.supplier_inquiry_id ||
                    po.supplier_quotation_id) && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong>Source references: </Text>
                      {po.bidAnalysisSources?.length ? (
                        <div style={{ marginTop: 8 }}>
                          {po.bidAnalysisSources.map((src) => (
                            <div key={src.bid_analysis_id} style={{ marginBottom: 4 }}>
                              <Button
                                type="link"
                                size="small"
                                onClick={() => navigate(`/scm/procurement/bid-analyses/${src.bid_analysis_id}`)}
                              >
                                Bid analysis {src.analysis_number ?? src.bid_analysis_id.slice(0, 8)}
                              </Button>
                              {src.supplier_inquiry_id && (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() =>
                                    navigate(`/scm/procurement/supplier-inquiries/${src.supplier_inquiry_id}`)
                                  }
                                >
                                  Inquiry {src.inquiry_number ?? ''}
                                </Button>
                              )}
                              {src.supplier_quotation_ids.map((qid, idx) => (
                                <Button
                                  key={qid}
                                  type="link"
                                  size="small"
                                  onClick={() => navigate(`/scm/procurement/supplier-quotations/${qid}`)}
                                >
                                  Quotation {src.quotation_numbers[idx] ?? ''}
                                </Button>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          {po.bid_analysis_id && (
                            <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/bid-analyses/${po.bid_analysis_id}`)}>
                              Bid analysis
                            </Button>
                          )}
                          {po.supplier_inquiry_id && (
                            <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-inquiries/${po.supplier_inquiry_id}`)}>
                              Inquiry
                            </Button>
                          )}
                          {po.supplier_quotation_id && (
                            <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-quotations/${po.supplier_quotation_id}`)}>
                              Quotation
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {po.amendment_of_po_id && (
                    <p style={{ marginTop: 8 }}>
                      <Text type="secondary">Amendment of: </Text>
                      <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-orders/${po.amendment_of_po_id}`)}>
                        {po.amendmentOf?.po_number ? `Original ${po.amendmentOf.po_number}` : 'View original PO'}
                      </Button>
                      {po.amendmentOf?.po_status && (
                        <Tag style={{ marginLeft: 8 }}>{po.amendmentOf.po_status}</Tag>
                      )}
                    </p>
                  )}
                  {po.parent_po_id && (
                    <p style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        {envelopeChildActionLabel(po.envelopeParent?.po_type) === 'call-off'
                          ? 'Call-off of: '
                          : 'Release of: '}
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/scm/procurement/purchase-orders/${po.parent_po_id}`)}
                      >
                        {po.envelopeParent?.po_number
                          ? `${normalizePoType(po.envelopeParent.po_type)} ${po.envelopeParent.po_number}`
                          : 'View parent PO'}
                      </Button>
                    </p>
                  )}
                  {(po.amendments?.length ?? 0) > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Amendment revisions:</Text>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {po.amendments!.map((a) => (
                          <li key={a.id}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => navigate(`/scm/procurement/purchase-orders/${a.id}`)}
                            >
                              {a.po_number}
                              {a.revision_number != null ? ` (${displayPoRevisionLabel(a.revision_number)})` : ''}
                            </Button>
                            <Tag style={{ marginLeft: 8 }}>{a.po_status}</Tag>
                            {a.approval_status && a.approval_status !== a.po_status && (
                              <Text type="secondary" style={{ marginLeft: 8 }}>
                                Approval: {a.approval_status}
                              </Text>
                            )}
                          </li>
                        ))}
                      </ul>
                      {po.amendments!.some((a) => a.po_status === 'Draft' || a.approval_status === 'Pending') && (
                        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                          This PO stays active for receipts until the amendment is approved; then the original is closed automatically.
                        </Typography.Paragraph>
                      )}
                    </div>
                  )}
                  {(po.envelopeChildren?.length ?? 0) > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        {envelopeAction === 'call-off' ? 'Call-offs:' : 'Releases:'}
                      </Text>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {po.envelopeChildren!.map((c) => (
                          <li key={c.id}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => navigate(`/scm/procurement/purchase-orders/${c.id}`)}
                            >
                              {c.po_number}
                            </Button>
                            <Tag style={{ marginLeft: 8 }}>{c.po_status}</Tag>
                            {c.delivery_date ? (
                              <Text type="secondary" style={{ marginLeft: 8 }}>
                                Delivery {dayjs(c.delivery_date).format('DD MMM YYYY')}
                              </Text>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                        Receive goods against release/call-off documents only — not against this envelope.
                      </Typography.Paragraph>
                    </div>
                  )}
                </>
              ),
            },
            {
              key: 'lines',
              label: `Line items (${items.length})`,
              children: (
                <>
                  {budgetCheckQ.isFetching && !budgetCheckQ.data ? (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Checking budget…
                    </Text>
                  ) : null}
                  <Table
                  size="small"
                  rowKey="id"
                  dataSource={items}
                  pagination={false}
                  scroll={{ x: 1100 }}
                  expandable={lineSpecificationsExpandable<PurchaseOrderItemDTO>({
                    getValue: (r) => r.specifications,
                    label: 'Technical specification',
                  })}
                  rowClassName={(row: any) =>
                    getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
                  }
                  columns={[
                    { title: '#', dataIndex: 'line_number', width: 50 },
                    {
                      title: 'Item',
                      dataIndex: 'item_code',
                      width: 140,
                      render: (code: string, row: any) => (
                        <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
                      ),
                    },
                    {
                      title: 'Description',
                      dataIndex: 'item_description',
                      ellipsis: true,
                      width: 220,
                      render: (desc: string, row: PurchaseOrderItemDTO) =>
                        formatItemDescriptionWithProfile(desc, {
                          ...(row as any),
                          item_description:
                            (row as any).catalog_item_description ?? undefined,
                        }) || desc || '—',
                    },
                    {
                      title: 'Supplier Description',
                      dataIndex: 'supplier_description',
                      ellipsis: true,
                      width: 220,
                      render: (v: string, row: PurchaseOrderItemDTO) => v || row.item_description || '—',
                    },
                    {
                      title: 'Stock qty',
                      key: 'stock_quantity',
                      width: 180,
                      render: (_: unknown, row: PurchaseOrderItemDTO) =>
                        row.stock_quantity != null
                          ? [row.stock_dimensions, `${Number(row.stock_quantity)} ${row.stock_uom || ''}`.trim()]
                              .filter(Boolean)
                              .join(' · ')
                          : '—',
                    },
                    { title: 'Project', key: 'project', width: 120, render: (_: unknown, row: any) => projectLabel(row) },
                    {
                      title: 'WBS',
                      key: 'wbs',
                      width: 140,
                      ellipsis: true,
                      render: (_: unknown, row: any) => (
                        <BudgetCodeLabel
                          projectId={row.project_id || po.project_id}
                          nodeId={row.wbs_node_id}
                          kind="wbs"
                        />
                      ),
                    },
                    {
                      title: 'Cat',
                      dataIndex: 'cost_category',
                      width: 90,
                      render: (v: string) => v || '—',
                    },
                    {
                      title: 'Budget',
                      key: 'budget_status',
                      width: 130,
                      render: (_: unknown, row: any) => (
                        <BudgetSpendStatus
                          projectId={row.project_id || po.project_id}
                          wbsNodeId={row.wbs_node_id}
                          cbsCodeId={row.cbs_code_id}
                          costCategory={row.cost_category || 'Material'}
                          requestedAmount={
                            Number(row.net_amount) ||
                            Number(row.line_total) ||
                            Number(row.unit_price || 0) * Number(row.ordered_quantity || 0)
                          }
                          excludeSourceType="purchase_order"
                          excludeSourceId={id}
                          compact
                        />
                      ),
                    },
                    { title: 'Qty', dataIndex: 'ordered_quantity', width: 70, align: 'right', render: (v: number) => Number(v).toLocaleString() },
                    ...(isEnvelopeParent
                      ? [
                          {
                            title: 'Released',
                            dataIndex: 'released_quantity',
                            width: 80,
                            align: 'right' as const,
                            render: (v: number, row: { ordered_quantity?: number }) => {
                              const released = Number(v ?? 0);
                              const ordered = Number(row.ordered_quantity ?? 0);
                              const rem = Math.max(0, ordered - released);
                              return (
                                <Tooltip title={`Remaining ${rem}`}>
                                  <span>{released.toLocaleString()}</span>
                                </Tooltip>
                              );
                            },
                          },
                        ]
                      : []),
                    { title: 'UOM', dataIndex: 'uom', width: 55 },
                    ...((items ?? []).some((l: any) => l.theoretical_weight != null)
                      ? [
                          {
                            title: 'Theo. weight',
                            key: 'theoretical_weight',
                            width: 105,
                            align: 'right' as const,
                            render: (_: unknown, r: any) => (
                              <EngineeringWeightCell weight={r.theoretical_weight} snapshot={r.engineering_snapshot} />
                            ),
                          },
                        ]
                      : []),
                    {
                      title: 'Unit price',
                      dataIndex: 'unit_price',
                      width: 95,
                      align: 'right',
                      render: (v: number) => `${po.currency} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                    },
                    {
                      title: 'Discount %',
                      dataIndex: 'discount_percentage',
                      width: 85,
                      align: 'right',
                      render: (v: number) => (v != null ? Number(v).toFixed(1) : '0'),
                    },
                    {
                      title: 'Discount',
                      dataIndex: 'discount_amount',
                      width: 95,
                      align: 'right',
                      render: (v: number) => `${po.currency} ${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                    },
                    {
                      title: 'Tax type',
                      key: 'tax_type',
                      width: 90,
                      render: (_: unknown, record: any) => getTaxTypeLabel(record.vat_percentage, record.vat_applicable),
                    },
                    {
                      title: 'Tax',
                      dataIndex: 'vat_amount',
                      width: 95,
                      align: 'right',
                      render: (v: number) => `${po.currency} ${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'line_total',
                      width: 100,
                      align: 'right',
                      render: (v: number) => (
                        <Text strong>
                          {po.currency} {Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Text>
                      ),
                    },
                    ...(Number(po.total_overhead ?? 0) > 0
                      ? [
                          {
                            title: 'Allocated OH',
                            dataIndex: 'allocated_overhead_amount',
                            width: 100,
                            align: 'right' as const,
                            render: (v: number) =>
                              `${po.currency} ${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                          },
                          {
                            title: 'Landed unit',
                            dataIndex: 'landed_unit_cost',
                            width: 100,
                            align: 'right' as const,
                            render: (v: number) =>
                              `${po.currency} ${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                          },
                        ]
                      : []),
                  ]}
                />
                </>
              ),
            },
            ...((po.notes || po.terms_and_conditions || po.special_instructions)
              ? [
                  {
                    key: 'notes',
                    label: 'Notes & terms',
                    children: (
                      <>
                        {po.notes && <p>{po.notes}</p>}
                        {po.terms_and_conditions && (
                          <p>
                            <strong>Terms:</strong> {po.terms_and_conditions}
                          </p>
                        )}
                        {po.special_instructions && (
                          <p>
                            <strong>Special instructions:</strong> {po.special_instructions}
                          </p>
                        )}
                      </>
                    ),
                  } as const,
                ]
              : []),
          ]}
        />
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_ORDER}
          entityId={id}
          readOnly={po.po_status === 'Cancelled' || po.po_status === 'Closed'}
          queryKeyPrefix={['documents', 'purchase-order']}
        />
      </WorkspaceLayout>

      <BudgetExceedanceRequestModal
        open={exceedanceOpen}
        onClose={() => setExceedanceOpen(false)}
        projectId={exceedanceProjectId}
        sourceEntityType="purchase_order"
        sourceEntityId={id!}
        lines={exceedanceLines}
        checkResult={exceedanceCheck as any}
        onSubmitted={() => {
          queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
          queryClient.invalidateQueries({ queryKey: ['po-budget-check', id] });
        }}
      />
      <Modal
        title="Department required for advance request"
        open={sarDeptModalOpen}
        onCancel={() => setSarDeptModalOpen(false)}
        confirmLoading={sarCreating}
        onOk={async () => {
          if (!sarDepartmentId?.trim()) {
            message.warning('Department is required');
            return;
          }
          setSarCreating(true);
          try {
            const sar = await supplierAdvanceRequestService.createFromPo(id!, {
              department_id: sarDepartmentId.trim(),
            });
            message.success(`Created ${sar.advance_request_number}`);
            setSarDeptModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['po-advance-summary', id] });
            navigate(`/scm/procurement/supplier-advance-requests/${sar.id}`);
          } catch (e: any) {
            message.error(e?.response?.data?.message || e?.message || 'Failed to create advance request');
          } finally {
            setSarCreating(false);
          }
        }}
        okText="Create advance request"
      >
        <DepartmentSelect
          value={sarDepartmentId}
          onChange={(value) => setSarDepartmentId(value || undefined)}
          style={{ width: '100%' }}
          placeholder="Select department"
        />
      </Modal>
      {isEnvelopeParent ? (
        <CreateEnvelopeChildModal
          open={envelopeChildOpen}
          parentPo={po}
          parentItems={items}
          onClose={() => setEnvelopeChildOpen(false)}
          onCreated={(child) => {
            queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
            if (child?.id) navigate(`/scm/procurement/purchase-orders/${child.id}`);
          }}
        />
      ) : null}
    </div>
  );
};

export default PurchaseOrderDetailPage;
